import { create } from 'zustand';
import { 
  DEFAULT_USERS, 
  DEFAULT_GROUPS, 
  DEFAULT_PERMISSIONS,
  generateId,
  getUserEffectivePermissions 
} from '../types';
import { createUser as createUserAPI } from '../../../api/admin';

// Mock delay for async operations
const mockDelay = (ms = 300) => new Promise(resolve => setTimeout(resolve, ms));

// Create the main store
const useAdminStore = create((set, get) => ({
  // Data state
  users: DEFAULT_USERS,
  groups: DEFAULT_GROUPS,
  permissions: DEFAULT_PERMISSIONS,
  
  // Current user state (for demo purposes)
  currentUser: {
    id: 'u-1',
    email: 'admin@avdcare.com',
    displayName: 'System Administrator',
    role: 'admin',
    effectivePermissions: ['*']
  },
  
  // Loading states
  loading: {
    users: false,
    groups: false,
    permissions: false
  },
  
  // Error states
  errors: {
    users: null,
    groups: null,
    permissions: null
  },
  
  // Mock latency setting (for dev toolbar)
  latency: 300,
  
  // Local storage persistence toggle
  persistData: false,

  // Actions for users
  fetchUsers: async () => {
    set(state => ({
      loading: { ...state.loading, users: true },
      errors: { ...state.errors, users: null }
    }));
    
    try {
      await mockDelay(get().latency);
      // In real app, this would be an API call
      // For now, we just use the existing state
      set(state => ({
        loading: { ...state.loading, users: false }
      }));
    } catch (error) {
      set(state => ({
        loading: { ...state.loading, users: false },
        errors: { ...state.errors, users: error.message }
      }));
    }
  },

  createUser: async (userData) => {
    set(state => ({
      loading: { ...state.loading, users: true },
      errors: { ...state.errors, users: null }
    }));
    
    try {
      // Use real API call instead of mock
      const response = await createUserAPI(userData);
      
      const newUser = {
        id: response.data.user.id,
        displayName: response.data.user.name,
        email: response.data.user.email,
        role: response.data.user.role,
        status: response.data.user.status,
        groups: response.data.user.groups || [],
        permissions: response.data.user.permissions || [],
        createdAt: response.data.user.createdAt,
        lastLogin: null
      };
      
      set(state => ({
        users: [...state.users, newUser],
        loading: { ...state.loading, users: false }
      }));
      
      if (get().persistData) {
        localStorage.setItem('adminIAM_users', JSON.stringify(get().users));
      }
      
      return newUser;
    } catch (error) {
      set(state => ({
        loading: { ...state.loading, users: false },
        errors: { ...state.errors, users: error.message }
      }));
      throw error;
    }
  },

  updateUser: async (userId, userData) => {
    set(state => ({
      loading: { ...state.loading, users: true },
      errors: { ...state.errors, users: null }
    }));
    
    try {
      await mockDelay(get().latency);
      
      set(state => ({
        users: state.users.map(user => 
          user.id === userId ? { ...user, ...userData } : user
        ),
        loading: { ...state.loading, users: false }
      }));
      
      if (get().persistData) {
        localStorage.setItem('adminIAM_users', JSON.stringify(get().users));
      }
      
      return get().users.find(u => u.id === userId);
    } catch (error) {
      set(state => ({
        loading: { ...state.loading, users: false },
        errors: { ...state.errors, users: error.message }
      }));
      throw error;
    }
  },

  deleteUser: async (userId) => {
    set(state => ({
      loading: { ...state.loading, users: true },
      errors: { ...state.errors, users: null }
    }));
    
    try {
      await mockDelay(get().latency);
      
      set(state => ({
        users: state.users.filter(user => user.id !== userId),
        loading: { ...state.loading, users: false }
      }));
      
      // Also remove user from all groups
      set(state => ({
        groups: state.groups.map(group => ({
          ...group,
          members: group.members ? group.members.filter(id => id !== userId) : []
        }))
      }));
      
      if (get().persistData) {
        localStorage.setItem('adminIAM_users', JSON.stringify(get().users));
        localStorage.setItem('adminIAM_groups', JSON.stringify(get().groups));
      }
    } catch (error) {
      set(state => ({
        loading: { ...state.loading, users: false },
        errors: { ...state.errors, users: error.message }
      }));
      throw error;
    }
  },

  // Actions for groups
  fetchGroups: async () => {
    set(state => ({
      loading: { ...state.loading, groups: true },
      errors: { ...state.errors, groups: null }
    }));
    
    try {
      await mockDelay(get().latency);
      set(state => ({
        loading: { ...state.loading, groups: false }
      }));
    } catch (error) {
      set(state => ({
        loading: { ...state.loading, groups: false },
        errors: { ...state.errors, groups: error.message }
      }));
    }
  },

  createGroup: async (groupData) => {
    set(state => ({
      loading: { ...state.loading, groups: true },
      errors: { ...state.errors, groups: null }
    }));
    
    try {
      await mockDelay(get().latency);
      
      const newGroup = {
        id: generateId('g-'),
        ...groupData,
        permissions: groupData.permissions || [],
        members: groupData.members || []
      };
      
      set(state => ({
        groups: [...state.groups, newGroup],
        loading: { ...state.loading, groups: false }
      }));
      
      if (get().persistData) {
        localStorage.setItem('adminIAM_groups', JSON.stringify(get().groups));
      }
      
      return newGroup;
    } catch (error) {
      set(state => ({
        loading: { ...state.loading, groups: false },
        errors: { ...state.errors, groups: error.message }
      }));
      throw error;
    }
  },

  updateGroup: async (groupId, groupData) => {
    set(state => ({
      loading: { ...state.loading, groups: true },
      errors: { ...state.errors, groups: null }
    }));
    
    try {
      await mockDelay(get().latency);
      
      set(state => ({
        groups: state.groups.map(group => 
          group.id === groupId ? { ...group, ...groupData } : group
        ),
        loading: { ...state.loading, groups: false }
      }));
      
      if (get().persistData) {
        localStorage.setItem('adminIAM_groups', JSON.stringify(get().groups));
      }
      
      return get().groups.find(g => g.id === groupId);
    } catch (error) {
      set(state => ({
        loading: { ...state.loading, groups: false },
        errors: { ...state.errors, groups: error.message }
      }));
      throw error;
    }
  },

  deleteGroup: async (groupId) => {
    set(state => ({
      loading: { ...state.loading, groups: true },
      errors: { ...state.errors, groups: null }
    }));
    
    try {
      await mockDelay(get().latency);
      
      // Remove group from all users
      set(state => ({
        users: state.users.map(user => ({
          ...user,
          groups: user.groups.filter(id => id !== groupId)
        })),
        groups: state.groups.filter(group => group.id !== groupId),
        loading: { ...state.loading, groups: false }
      }));
      
      if (get().persistData) {
        localStorage.setItem('adminIAM_users', JSON.stringify(get().users));
        localStorage.setItem('adminIAM_groups', JSON.stringify(get().groups));
      }
    } catch (error) {
      set(state => ({
        loading: { ...state.loading, groups: false },
        errors: { ...state.errors, groups: error.message }
      }));
      throw error;
    }
  },

  addUserToGroup: async (userId, groupId) => {
    try {
      await mockDelay(get().latency);
      
      // Add user to group members
      set(state => ({
        groups: state.groups.map(group => {
          if (group.id === groupId) {
            const members = group.members || [];
            if (!members.includes(userId)) {
              return { ...group, members: [...members, userId] };
            }
          }
          return group;
        })
      }));
      
      // Add group to user's groups
      set(state => ({
        users: state.users.map(user => {
          if (user.id === userId) {
            const groups = user.groups || [];
            if (!groups.includes(groupId)) {
              return { ...user, groups: [...groups, groupId] };
            }
          }
          return user;
        })
      }));
      
      if (get().persistData) {
        localStorage.setItem('adminIAM_users', JSON.stringify(get().users));
        localStorage.setItem('adminIAM_groups', JSON.stringify(get().groups));
      }
    } catch (error) {
      throw error;
    }
  },

  removeUserFromGroup: async (userId, groupId) => {
    try {
      await mockDelay(get().latency);
      
      // Remove user from group members
      set(state => ({
        groups: state.groups.map(group => {
          if (group.id === groupId) {
            const members = group.members || [];
            return { ...group, members: members.filter(id => id !== userId) };
          }
          return group;
        })
      }));
      
      // Remove group from user's groups
      set(state => ({
        users: state.users.map(user => {
          if (user.id === userId) {
            return { ...user, groups: user.groups.filter(id => id !== groupId) };
          }
          return user;
        })
      }));
      
      if (get().persistData) {
        localStorage.setItem('adminIAM_users', JSON.stringify(get().users));
        localStorage.setItem('adminIAM_groups', JSON.stringify(get().groups));
      }
    } catch (error) {
      throw error;
    }
  },

  // Actions for permissions
  fetchPermissions: async () => {
    set(state => ({
      loading: { ...state.loading, permissions: true },
      errors: { ...state.errors, permissions: null }
    }));
    
    try {
      await mockDelay(get().latency);
      set(state => ({
        loading: { ...state.loading, permissions: false }
      }));
    } catch (error) {
      set(state => ({
        loading: { ...state.loading, permissions: false },
        errors: { ...state.errors, permissions: error.message }
      }));
    }
  },

  createPermission: async (permissionData) => {
    set(state => ({
      loading: { ...state.loading, permissions: true },
      errors: { ...state.errors, permissions: null }
    }));
    
    try {
      await mockDelay(get().latency);
      
      const newPermission = {
        id: generateId('p-'),
        ...permissionData
      };
      
      set(state => ({
        permissions: [...state.permissions, newPermission],
        loading: { ...state.loading, permissions: false }
      }));
      
      if (get().persistData) {
        localStorage.setItem('adminIAM_permissions', JSON.stringify(get().permissions));
      }
      
      return newPermission;
    } catch (error) {
      set(state => ({
        loading: { ...state.loading, permissions: false },
        errors: { ...state.errors, permissions: error.message }
      }));
      throw error;
    }
  },

  deletePermission: async (permissionId) => {
    set(state => ({
      loading: { ...state.loading, permissions: true },
      errors: { ...state.errors, permissions: null }
    }));
    
    try {
      await mockDelay(get().latency);
      
      const permission = get().permissions.find(p => p.id === permissionId);
      if (!permission) throw new Error('Permission not found');
      
      // Remove permission from all groups
      set(state => ({
        groups: state.groups.map(group => ({
          ...group,
          permissions: group.permissions.filter(key => key !== permission.key)
        })),
        permissions: state.permissions.filter(p => p.id !== permissionId),
        loading: { ...state.loading, permissions: false }
      }));
      
      if (get().persistData) {
        localStorage.setItem('adminIAM_permissions', JSON.stringify(get().permissions));
        localStorage.setItem('adminIAM_groups', JSON.stringify(get().groups));
      }
    } catch (error) {
      set(state => ({
        loading: { ...state.loading, permissions: false },
        errors: { ...state.errors, permissions: error.message }
      }));
      throw error;
    }
  },

  // Utility methods
  getUserById: (userId) => {
    return get().users.find(user => user.id === userId);
  },

  getGroupById: (groupId) => {
    return get().groups.find(group => group.id === groupId);
  },

  getPermissionById: (permissionId) => {
    return get().permissions.find(permission => permission.id === permissionId);
  },

  getUserGroups: (userId) => {
    const user = get().getUserById(userId);
    if (!user || !user.groups) return [];
    return get().groups.filter(group => user.groups.includes(group.id));
  },

  getGroupMembers: (groupId) => {
    const group = get().getGroupById(groupId);
    if (!group || !group.members) return [];
    return get().users.filter(user => group.members.includes(user.id));
  },

  getUserEffectivePermissions: (userId) => {
    const user = get().getUserById(userId);
    if (!user) return [];
    return getUserEffectivePermissions(user, get().groups);
  },

  // Dev toolbar actions
  setCurrentUserRole: (role) => {
    const permissions = role === 'admin' 
      ? ['*'] 
      : ['users.read', 'groups.read', 'permissions.read'];
    
    set(state => ({
      currentUser: {
        ...state.currentUser,
        role,
        effectivePermissions: permissions
      }
    }));
  },

  setLatency: (ms) => {
    set({ latency: ms });
  },

  setPersistData: (persist) => {
    set({ persistData: persist });
    
    if (persist) {
      // Save current data to localStorage
      localStorage.setItem('adminIAM_users', JSON.stringify(get().users));
      localStorage.setItem('adminIAM_groups', JSON.stringify(get().groups));
      localStorage.setItem('adminIAM_permissions', JSON.stringify(get().permissions));
    } else {
      // Clear localStorage
      localStorage.removeItem('adminIAM_users');
      localStorage.removeItem('adminIAM_groups');
      localStorage.removeItem('adminIAM_permissions');
    }
  },

  // Load data from localStorage
  loadPersistedData: () => {
    try {
      const users = localStorage.getItem('adminIAM_users');
      const groups = localStorage.getItem('adminIAM_groups');
      const permissions = localStorage.getItem('adminIAM_permissions');
      
      set(state => ({
        users: users ? JSON.parse(users) : state.users,
        groups: groups ? JSON.parse(groups) : state.groups,
        permissions: permissions ? JSON.parse(permissions) : state.permissions
      }));
    } catch (error) {
      console.error('Failed to load persisted data:', error);
    }
  },

  // Reset to default data
  resetData: () => {
    set({
      users: DEFAULT_USERS,
      groups: DEFAULT_GROUPS,
      permissions: DEFAULT_PERMISSIONS
    });
    
    if (get().persistData) {
      localStorage.setItem('adminIAM_users', JSON.stringify(DEFAULT_USERS));
      localStorage.setItem('adminIAM_groups', JSON.stringify(DEFAULT_GROUPS));
      localStorage.setItem('adminIAM_permissions', JSON.stringify(DEFAULT_PERMISSIONS));
    }
  }
}));

export default useAdminStore;
